import { useState, useEffect } from 'react'
import { getPrompt, updatePrompt, activatePrompt } from '@/services/promptsApi'
import { PromptEditForm } from './PromptEditForm'
import type { Prompt, UpdatePromptData } from '@/types'

interface PromptDetailProps {
  promptId: string
  onBack: () => void
}

export function PromptDetail({ promptId, onBack }: PromptDetailProps) {
  const [prompt, setPrompt] = useState<Prompt | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const fetchPrompt = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await getPrompt(promptId)
      setPrompt(data)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load prompt'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchPrompt()
  }, [promptId])

  const handleSave = async (data: UpdatePromptData) => {
    setIsSaving(true)
    setSaveError(null)
    try {
      const updated = await updatePrompt(promptId, data)
      setPrompt(updated)
      setIsEditing(false)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to update prompt')
    } finally {
      setIsSaving(false)
    }
  }

  const handleActivate = async () => {
    setIsSaving(true)
    setSaveError(null)
    try {
      const updated = await activatePrompt(promptId)
      setPrompt(updated)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to activate prompt')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="p-6" data-testid="detail-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
          <div className="h-4 bg-gray-200 rounded w-3/4" />
        </div>
      </div>
    )
  }

  if (error || !prompt) {
    return (
      <div className="p-6" data-testid="detail-error">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load prompt</p>
          <button
            onClick={onBack}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Go back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6" data-testid="prompt-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="text-gray-500 hover:text-gray-700"
            data-testid="back-btn"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <h2 className="text-xl font-semibold text-gray-900">{prompt.name}</h2>
          {prompt.is_active && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Active
            </span>
          )}
        </div>
        {!isEditing && (
          <div className="flex gap-2">
            {!prompt.is_active && (
              <button
                onClick={handleActivate}
                disabled={isSaving}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                data-testid="activate-btn"
              >
                {isSaving ? 'Activating...' : 'Activate'}
              </button>
            )}
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              data-testid="edit-btn"
            >
              Edit
            </button>
          </div>
        )}
      </div>

      {/* Success message */}
      {saveSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="success-message">
          <p className="text-green-800">Prompt updated successfully</p>
        </div>
      )}

      {/* Error message */}
      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="error-message">
          <p className="text-red-800">{saveError}</p>
        </div>
      )}

      {/* Main content */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        {isEditing ? (
          <PromptEditForm
            prompt={prompt}
            onSave={handleSave}
            onCancel={() => setIsEditing(false)}
            isSaving={isSaving}
          />
        ) : (
          <div className="space-y-6" data-testid="prompt-view">
            {/* Name */}
            <div>
              <h3 className="text-sm font-medium text-gray-500">Prompt Name</h3>
              <p className="mt-1 text-lg text-gray-900">{prompt.name}</p>
            </div>

            {/* Use Case */}
            <div>
              <h3 className="text-sm font-medium text-gray-500">Use Case</h3>
              <p className="mt-1 text-sm text-gray-900">{prompt.use_case}</p>
            </div>

            {/* Model ID */}
            <div>
              <h3 className="text-sm font-medium text-gray-500">Model ID</h3>
              <p className="mt-1 text-sm text-gray-900 font-mono">{prompt.model_id}</p>
            </div>

            {/* Prompt Text */}
            <div>
              <h3 className="text-sm font-medium text-gray-500">Prompt Text</h3>
              <pre className="mt-2 p-4 bg-gray-50 rounded-md text-sm whitespace-pre-wrap overflow-x-auto max-h-96 overflow-y-auto">
                {prompt.prompt_text}
              </pre>
            </div>

            {/* Timestamps */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Created</h3>
                <p className="mt-1 text-sm text-gray-700">
                  {prompt.created_at
                    ? new Date(prompt.created_at).toLocaleString()
                    : '—'}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Last Updated</h3>
                <p className="mt-1 text-sm text-gray-700">
                  {prompt.updated_at
                    ? new Date(prompt.updated_at).toLocaleString()
                    : '—'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
